// src/services/activeCallTabCoordinator.js
//
// Cross-tab active-call coordination.
//
// Problem this solves:
//   The Twilio Voice Device registers under the agent's identity (their email).
//   Twilio only allows ONE active registration per identity. If the user opens
//   a second tab on the same domain, the new tab's `voiceCallService.initDevice()`
//   produces a fresh registration that silently kicks out the call-hosting tab,
//   dropping audio.
//
// Approach:
//   - The tab hosting an active call ("owner") publishes a heartbeat on a
//     BroadcastChannel + localStorage shadow record (so it survives tab
//     close/reopen across the heartbeat window).
//   - Other tabs ("observers") subscribe and expose `hasActiveCallElsewhere()`.
//   - Observers consult this before calling `initDevice()` and before the
//     navigation guard chooses its modal language.
//   - On owner navigation/unload the record is cleared deterministically;
//     a stale heartbeat (>15s old) is also treated as cleared.
//
// This module is a thin singleton — no Vue dependencies — so it can be imported
// from stores, services, and the router alike without circular concerns.

const CHANNEL_NAME = 'active-call-tab-coord'
const STORAGE_KEY = 'gse_active_call_owner'
const HEARTBEAT_MS = 5000
const STALE_AFTER_MS = 15000
// On boot, if we hydrate a previous owner from localStorage, give that tab
// this much time to respond to our CALL_OWNER_QUERY with a fresh heartbeat.
// If no reply, treat the storage record as orphaned (the prior tab crashed
// without firing beforeunload) and clear it so we're not blocked indefinitely.
const HYDRATED_OWNER_VERIFY_MS = 2500

let channel = null
let myTabId = null
let heartbeatTimer = null
let ownedCallSid = null

// Latest snapshot of the call-hosting tab as advertised by other tabs.
// `null` means no other tab has an active call.
let externalOwner = null // { tabId, callSid, lastSeen }

// Tab IDs we've received a fresh heartbeat from since boot. Used to verify
// that a hydrated-from-storage owner is still alive.
const heartbeatTabIds = new Set()

const listeners = new Set()

function now() {
  return Date.now()
}

function emitChange() {
  const snap = getExternalOwner()
  for (const fn of listeners) {
    try {
      fn(snap)
    } catch {
      /* listener errors must not break others */
    }
  }
}

function readStorageOwner() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.tabId || !parsed?.callSid || !parsed?.lastSeen) return null
    if (now() - parsed.lastSeen > STALE_AFTER_MS) return null
    return parsed
  } catch {
    return null
  }
}

function writeStorageOwner(record) {
  try {
    if (record) localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* private mode / quota / SSR — ignore */
  }
}

function ensureInit() {
  if (channel) return
  if (typeof BroadcastChannel === 'undefined') return // SSR / very old browsers
  myTabId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `tab_${Math.random().toString(36).slice(2)}`
  channel = new BroadcastChannel(CHANNEL_NAME)

  channel.onmessage = ev => {
    const msg = ev.data || {}
    if (!msg.type || msg.tabId === myTabId) return
    if (msg.type === 'CALL_OWNER_HEARTBEAT') {
      heartbeatTabIds.add(msg.tabId)
      externalOwner = { tabId: msg.tabId, callSid: msg.callSid, lastSeen: now() }
      emitChange()
    } else if (msg.type === 'CALL_OWNER_CLEARED') {
      if (externalOwner && externalOwner.tabId === msg.tabId) {
        externalOwner = null
        emitChange()
      }
    } else if (msg.type === 'CALL_OWNER_QUERY') {
      // A new tab is asking who owns the call. If we own one, immediately
      // re-broadcast our heartbeat so they don't have to wait HEARTBEAT_MS.
      if (ownedCallSid) {
        channel.postMessage({ type: 'CALL_OWNER_HEARTBEAT', tabId: myTabId, callSid: ownedCallSid })
      }
    }
  }

  // Hydrate from any persisted owner record (e.g. previous owner tab is still
  // mid-heartbeat but hasn't pinged us yet).
  externalOwner = readStorageOwner()

  // Probe: ask any owners to announce themselves immediately.
  try {
    channel.postMessage({ type: 'CALL_OWNER_QUERY', tabId: myTabId })
  } catch {
    /* ignore */
  }

  // Verify hydrated owner is still alive. If a tab crashed (kernel panic,
  // force-quit, OS sleep) without firing beforeunload, its storage record
  // sits there for STALE_AFTER_MS telling us "I own a call." If no fresh
  // heartbeat arrives within HYDRATED_OWNER_VERIFY_MS, clear it so this tab
  // isn't blocked from initializing voice for the user's session.
  if (externalOwner) {
    const hydratedTabId = externalOwner.tabId
    setTimeout(() => {
      if (heartbeatTabIds.has(hydratedTabId)) return // owner is alive
      if (!externalOwner || externalOwner.tabId !== hydratedTabId) return // already replaced
      // No heartbeat reply — owner tab is gone. Clear the orphan.
      // eslint-disable-next-line no-console
      console.warn(
        '[ActiveCallCoord] Hydrated owner',
        hydratedTabId,
        'never replied — clearing orphan record'
      )
      externalOwner = null
      writeStorageOwner(null)
      emitChange()
    }, HYDRATED_OWNER_VERIFY_MS)
  }

  // Clear our claim deterministically if the tab is closing.
  window.addEventListener('beforeunload', () => {
    if (ownedCallSid) {
      try {
        channel.postMessage({ type: 'CALL_OWNER_CLEARED', tabId: myTabId })
      } catch {
        /* ignore */
      }
      writeStorageOwner(null)
    }
  })
}

function startHeartbeat() {
  stopHeartbeat()
  heartbeatTimer = setInterval(() => {
    if (!ownedCallSid || !channel) return
    const record = { tabId: myTabId, callSid: ownedCallSid, lastSeen: now() }
    writeStorageOwner(record)
    try {
      channel.postMessage({ type: 'CALL_OWNER_HEARTBEAT', ...record })
    } catch {
      /* ignore */
    }
  }, HEARTBEAT_MS)
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

/**
 * Publish that THIS tab is the active-call owner. Pass `null` to clear.
 * Idempotent — safe to call from a watcher whose value can flip many times.
 */
export function publishActiveCall(callSid) {
  ensureInit()
  if (callSid && ownedCallSid === callSid) return
  if (!callSid && !ownedCallSid) return

  ownedCallSid = callSid || null
  if (ownedCallSid) {
    const record = { tabId: myTabId, callSid: ownedCallSid, lastSeen: now() }
    writeStorageOwner(record)
    try {
      channel?.postMessage({ type: 'CALL_OWNER_HEARTBEAT', ...record })
    } catch {
      /* ignore */
    }
    startHeartbeat()
  } else {
    stopHeartbeat()
    writeStorageOwner(null)
    try {
      channel?.postMessage({ type: 'CALL_OWNER_CLEARED', tabId: myTabId })
    } catch {
      /* ignore */
    }
  }
}

/**
 * Returns `{ tabId, callSid, lastSeen }` if a *different* tab has advertised an
 * active call within the staleness window, else `null`. Reads from both the
 * BroadcastChannel cache (fastest) and the localStorage shadow (cross-context
 * fallback).
 */
export function getExternalOwner() {
  ensureInit()
  if (externalOwner && now() - externalOwner.lastSeen <= STALE_AFTER_MS) {
    return { ...externalOwner }
  }
  const fromStorage = readStorageOwner()
  if (fromStorage && fromStorage.tabId !== myTabId) return fromStorage
  return null
}

export function hasActiveCallElsewhere() {
  return getExternalOwner() !== null
}

/** Returns the callSid this tab claims to own, or null. */
export function getOwnedCallSid() {
  return ownedCallSid
}

/**
 * Subscribe to changes in the *external* owner. Fires immediately with the
 * current snapshot, then whenever it changes.
 */
export function onExternalOwnerChange(cb) {
  ensureInit()
  listeners.add(cb)
  try {
    cb(getExternalOwner())
  } catch {
    /* ignore */
  }
  return () => listeners.delete(cb)
}
